import { IntlShape, MessageDescriptor } from 'react-intl';
import { STUDIO_THEMES } from 'fym-shared/src/music24/studioThemes';
import { kitVariantLabel } from '../kitLabels';
import { studioThemeMessages } from '../studio/studioThemeMessages';
import { AssetViewerItem } from './viewerItems';
import { viewerMessages } from './viewerMessages';
import { watermarkFileMessages } from '../../watermarkUpsell/files/watermarkFileMessages';

const KILOBYTE = 1024;
const MEGABYTE = 1024 * 1024;

/** Megabytes with one decimal below 10 MB, whole above ("1.8", "84"). Pure. */
export function megabytes(bytes: number): number {
  const value = bytes / MEGABYTE;
  return value < 10 ? Math.round(value * 10) / 10 : Math.round(value);
}

/** Whole kilobytes, at least 1 ("312"). Pure. */
export function kilobytes(bytes: number): number {
  return Math.max(1, Math.round(bytes / KILOBYTE));
}

/**
 * The size of a file: KB below 1 MB ("312 KB"), else MB ("1.8 MB", "84 MB"). A 540 × 960 preview
 * read "0 MB" before (onboarding review 2026-10-19).
 */
export function formatFileSize(intl: IntlShape, bytes: number): string {
  if (bytes < MEGABYTE) {
    return intl.formatMessage(viewerMessages.kilobytes, { value: intl.formatNumber(kilobytes(bytes)) });
  }
  return intl.formatMessage(viewerMessages.megabytes, { value: intl.formatNumber(megabytes(bytes)) });
}

/** The length of a video as m:ss ("3:54"). Pure. */
export function formatLength(seconds: number): string {
  const whole = Math.max(0, Math.round(seconds));
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`;
}

/** True when the asset type makes more than one file, so the label names the variant. */
function namesVariant(item: AssetViewerItem): boolean {
  if (item.variant === null || item.specId === null) {
    return false;
  }
  return item.themeId === 'story' || item.themeId === 'feedPost' || STUDIO_THEMES[item.themeId].outputs.length > 1;
}

/** The asset label of the side panel: "Story (Out now)", "Cover sizes (3000 × 3000)", "AI reel". */
export function viewerAssetLabel(intl: IntlShape, item: AssetViewerItem): string {
  const name = intl.formatMessage(studioThemeMessages[item.themeId].name);
  if (!namesVariant(item) || item.variant === null || item.specId === null) {
    return name;
  }
  return `${name} (${kitVariantLabel(intl, { variant: item.variant, specId: item.specId })})`;
}

export interface ViewerFact {
  key: 'size' | 'watermark' | 'length' | 'file' | 'version' | 'made';
  label: MessageDescriptor;
  value: string;
  /** A fact that a plan makes better: the preview size and the watermark show in pink (trial-upsell a3). */
  bad?: boolean;
}

/** The file fact: "JPG · 1.8 MB", or the type alone when the size is not known. */
function fileValue(intl: IntlShape, item: AssetViewerItem): string {
  if (item.sizeBytes === null) {
    return item.fileType;
  }
  const size = formatFileSize(intl, item.sizeBytes);
  return `${item.fileType} · ${size}`;
}

/**
 * The facts of the side panel (design a1, a2; trial-upsell a3): size, watermark, length (a video), file, version ("3 ·
 * latest"), made. A fact with no value is left out.
 */
export function viewerFacts(intl: IntlShape, item: AssetViewerItem): ViewerFact[] {
  const facts: ViewerFact[] = [];
  if (item.width !== null && item.height !== null) {
    facts.push(
      item.preview
        ? {
            key: 'size',
            label: viewerMessages.factSize,
            value: intl.formatMessage(watermarkFileMessages.factPreviewSize, {
              width: item.width,
              height: item.height,
            }),
            bad: true,
          }
        : { key: 'size', label: viewerMessages.factSize, value: `${item.width} × ${item.height}` }
    );
  }
  if (item.watermark) {
    facts.push({
      key: 'watermark',
      label: watermarkFileMessages.factWatermark,
      value: intl.formatMessage(watermarkFileMessages.factWatermarkValue),
      bad: true,
    });
  }
  if (item.mediaKind === 'video' && item.durationSec !== null) {
    facts.push({ key: 'length', label: viewerMessages.factLength, value: formatLength(item.durationSec) });
  }
  facts.push({ key: 'file', label: viewerMessages.factFile, value: fileValue(intl, item) });
  if (item.version !== null) {
    facts.push({
      key: 'version',
      label: viewerMessages.factVersion,
      value: item.isLatest
        ? intl.formatMessage(viewerMessages.versionLatest, { version: item.version })
        : String(item.version),
    });
  }
  facts.push({
    key: 'made',
    label: viewerMessages.factMade,
    value: intl.formatDate(item.madeAt, { day: 'numeric', month: 'short', year: 'numeric' }),
  });
  return facts;
}

/** The one-line facts of the narrow viewer (design a6): "1080 × 1920 · 1.8 MB · v3". */
export function viewerFactsLine(intl: IntlShape, item: AssetViewerItem): string {
  const parts: string[] = [];
  if (item.width !== null && item.height !== null) {
    parts.push(`${item.width} × ${item.height}`);
  }
  if (item.mediaKind === 'video' && item.durationSec !== null) {
    parts.push(formatLength(item.durationSec));
  }
  if (item.sizeBytes !== null) {
    parts.push(formatFileSize(intl, item.sizeBytes));
  }
  if (item.version !== null) {
    parts.push(intl.formatMessage(viewerMessages.versionShort, { version: item.version }));
  }
  return parts.join(' · ');
}
